import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Grid,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import ReportLineChart from '@/components/reports/ReportLineChart'
import ReportBarChart from '@/components/reports/ReportBarChart'
import ReportDataTable from '@/components/reports/ReportDataTable'
import { METRIC_TABLE_COLUMNS } from '@/components/reports/reportTableColumns'
import {
  helpdeskReportService,
  type ReportFilters,
  type ReportsOverviewResponse,
} from '@/lib/services/helpdeskReportService'

const REPORT_LINKS = [
  { label: 'SD KPI', to: '/reports/sd-kpi', desc: 'Acknowledgement, closing, creation metrics' },
  { label: 'FE KPI', to: '/reports/fe-kpi', desc: 'Task acceptance, resolution, APT' },
  { label: 'APR Durations', to: '/reports/apr-durations', desc: 'Lifecycle processing times' },
  { label: 'SLA Compliance', to: '/reports/sla-compliance', desc: 'Ticket and task SLA performance' },
  { label: 'Agent Performance', to: '/reports/agent-performance', desc: 'Leaderboard by load and FRT' },
  {
    label: 'SD Agent Productivity',
    to: '/reports/agent-productivity',
    desc: 'Sample-shaped RAW FD/FP downloads for offline reports',
  },
  {
    label: 'Ticket Productivity',
    to: '/reports/ticket-productivity',
    desc: 'Handling, daily, and monthly ticket productivity',
  },
  {
    label: 'Contact Center Productivity',
    to: '/reports/contact-center-productivity',
    desc: 'Telephony/WFM monthly pivot with CSV import',
  },
  { label: 'FE Attendance', to: '/reports/fe-attendance', desc: 'Field engineer attendance and job summary' },
  { label: 'Ticket Volume', to: '/reports/ticket-volume', desc: 'Trends and breakdowns' },
  { label: 'Historical Export', to: '/reports/historical-export', desc: 'CSV / Excel download' },
  { label: 'Task Reports', to: '/reports/task-reports', desc: 'Open, overdue, SLA & task completion' },
]

const VOLUME_LINE_SERIES = [
  { dataKey: 'created', name: 'Created', color: '#3182CE' },
  { dataKey: 'closed', name: 'Closed', color: '#38A169' },
]

const defaultOverview: ReportsOverviewResponse = {
  period_summary: {
    created: 0,
    closed: 0,
    net: 0,
    overdue_open: 0,
    unassigned_open: 0,
    sla_compliance_percent: 0,
    avg_first_response_seconds: 0,
  },
  volume_series: [],
  by_status: [],
  by_source: [],
  kpi_snapshot: [],
  filters: {},
}

function formatFirstResponseTime(seconds: number): string {
  if (seconds <= 0) return '—'
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  return `${Math.round(seconds)}s`
}

function formatFilterRange(filters: ReportFilters): string {
  if (filters.from && filters.to) return `${filters.from} – ${filters.to}`
  if (filters.from) return `From ${filters.from}`
  if (filters.to) return `Until ${filters.to}`
  return 'Last 30 days (default)'
}

export default function ReportsOverviewPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [overview, setOverview] = useState<ReportsOverviewResponse>(defaultOverview)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getOverview(filters)
      .then(setOverview)
      .catch(() => setOverview(defaultOverview))
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id])

  const summary = overview.period_summary
  const periodLabel = formatFilterRange(overview.filters ?? filters)

  const statusChartData = useMemo(
    () => overview.by_status.map((row) => ({ label: row.status, count: row.count })),
    [overview.by_status]
  )

  const volumeChartData = useMemo(
    () =>
      overview.volume_series.map((row) => ({
        date: row.date,
        created: row.created,
        closed: row.closed,
      })),
    [overview.volume_series]
  )

  const sourceChartData = useMemo(
    () =>
      overview.by_source.map((row) => ({
        label: row.source,
        count: row.count,
        color: row.color,
      })),
    [overview.by_source]
  )

  const kpiCards = [
    { label: 'Created', value: summary.created.toLocaleString() },
    { label: 'Closed', value: summary.closed.toLocaleString() },
    {
      label: 'Net change',
      value: `${summary.net >= 0 ? '+' : ''}${summary.net.toLocaleString()}`,
      color: summary.net >= 0 ? 'orange.600' : 'green.600',
    },
    { label: 'Overdue (open)', value: summary.overdue_open.toLocaleString(), color: 'red.600' },
    { label: 'SLA compliance', value: `${summary.sla_compliance_percent}%` },
    { label: 'Avg first response', value: formatFirstResponseTime(summary.avg_first_response_seconds) },
  ]

  return (
    <Box p={4}>
      <ReportPageHeader
        title="Reports Overview"
        subtitle="Period analytics and quick links to detailed reports"
        reportType="overview"
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Text fontSize="xs" color="gray.500" mb={4}>
        Showing data for: {periodLabel}
        {summary.unassigned_open > 0 ? ` · ${summary.unassigned_open} unassigned in period` : ''}
      </Text>

      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }}
        gap={3}
        mb={6}
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height="80px" />)
          : kpiCards.map((card) => (
              <Card key={card.label}>
                <CardBody py={3}>
                  <Stat>
                    <StatLabel fontSize="xs">{card.label}</StatLabel>
                    <StatNumber fontSize="xl" color={card.color}>
                      {card.value}
                    </StatNumber>
                  </Stat>
                </CardBody>
              </Card>
            ))}
      </Grid>

      <Box mb={6}>
        <ReportLineChart
          title="Ticket flow"
          subtitle="Created vs closed per day for the selected period"
          data={volumeChartData}
          series={VOLUME_LINE_SERIES}
          loading={loading}
        />
      </Box>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} mb={6}>
        <ReportBarChart
          title="Status distribution"
          subtitle="Tickets created in period, by current status"
          data={statusChartData}
          loading={loading}
        />
        <ReportBarChart
          title="Source channel"
          subtitle="How tickets entered the system"
          data={sourceChartData}
          loading={loading}
        />
      </Grid>

      <Box mb={6}>
        <ReportDataTable
          title="SD KPI snapshot"
          subtitle="Headline service desk metrics for this period"
          columns={METRIC_TABLE_COLUMNS}
          rows={overview.kpi_snapshot}
          loading={loading}
          loadingHeight="160px"
          getRowKey={(row) => row.metric}
          footer={
            <Text as={RouterLink} to="/reports/sd-kpi" fontSize="sm" color="blue.600" mt={3} display="inline-block">
              View full SD KPI report →
            </Text>
          }
        />
      </Box>

      <Box>
        <Heading size="sm" color="gray.600" mb={3}>
          Detailed reports
        </Heading>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
          {REPORT_LINKS.map((r) => (
            <Card key={r.to} as={RouterLink} to={r.to} _hover={{ boxShadow: 'md' }}>
              <CardBody py={3}>
                <Text fontWeight="semibold" color="blue.700" fontSize="sm">
                  {r.label}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {r.desc}
                </Text>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  )
}
